import type { BookingDraft } from '@/scopes/checkout/domain/types/bookingDraft'
import type { PublicationCode } from '@/scopes/common/domain/models'
import { useDecision } from '@optimizely/react-sdk'
import * as React from 'react'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { hasAnyExternalFlight } from '@/scopes/checkout/domain/policies/transports'
import { OPTIMIZELY_TESTS_KEYS } from '@/scopes/common/infrastructure/ab-testing/optimizely/types'
import { OptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'

const DEFAULT_FLAGS = {
  showMandatoryPaymentPage: false, // Keep this as default, because in the future we have to make some implementations after Polaris is in place
}
/**
 * Inner checkout flag decider.
 *
 * Future experiment example:
 * 1. Add a key to OPTIMIZELY_TESTS_KEYS in common infrastructure types.
 * 2. Evaluate with useDecision and needed overrideAttributes.
 * 3. Expose the boolean in provider value while keeping current keys backward compatible.
 */

/**
 * Inner component — only mounts after booking draft data is ready,
 * guaranteeing useDecision receives correct override attributes on first evaluation.
 */
function CheckoutOptimizelyFlagsDecider({ children, data }: React.PropsWithChildren<{ data: BookingDraft }>) {
  const publicationCode = useConfiguration('publicationCode') as PublicationCode
  const hasExternalFlights = hasAnyExternalFlight(data.transportInfo)?.toString() || 'false'

  const mandatoryPaymentContext = React.useMemo(
    () => ({ overrideAttributes: { publicationCode, hasExternalFlights } }),
    [publicationCode, hasExternalFlights],
  )

  const [{ enabled: showMandatoryPaymentPage = false }] = useDecision(
    OPTIMIZELY_TESTS_KEYS.SHOW_MANDATORY_PAYMENT_PAGE,
    { autoUpdate: false },
    mandatoryPaymentContext,
  )

  const [{ enabled: showMandatoryPaymentPageUds = false }] = useDecision(
    OPTIMIZELY_TESTS_KEYS.MANDATORY_PAYMENT_PAGE_UDS,
    { autoUpdate: false },
  )

  const value = React.useMemo(
    () => ({ showMandatoryPaymentPage, showMandatoryPaymentPageUds }),
    [showMandatoryPaymentPage, showMandatoryPaymentPageUds],
  )

  return <OptimizelyFlagContext value={value}>{children}</OptimizelyFlagContext>
}

/**
 * Guards against calling useDecision before booking draft data is available.
 * Children receive default flag values (all false) while the query is in-flight.
 */
export function CheckoutOptimizelyFlagsProvider({ children }: React.PropsWithChildren) {
  const { data } = useBookingDraftQuery()

  if (!data) {
    return <OptimizelyFlagContext value={DEFAULT_FLAGS}>{children}</OptimizelyFlagContext>
  }

  return <CheckoutOptimizelyFlagsDecider data={data}>{children}</CheckoutOptimizelyFlagsDecider>
}
